// Shared master activity list. Used by:
//   • Weekly Calendar & Journal page — "Today's activities" dropdown
//   • Reminders page — picker in each frequency section
//
// Grouped by category for scannability via <optgroup>; alphabetized within each group.
// `category` drives the calendar event-type icon + color.

export const ACTIVITY_GROUPS = [
  {
    group: "Training & Exercise",
    items: [
      { label: "Agility", category: "training" },
      { label: "Balance, proprioception", category: "training" },
      { label: "Carrot stretches / Cone circles", category: "training" },
      { label: "Cavaletti / pole work", category: "training" },
      { label: "Coursing", category: "training" },
      { label: "FastCat", category: "training" },
      { label: "Flyball", category: "training" },
      { label: "Nose Work", category: "training" },
      { label: "Obedience", category: "training" },
      { label: "Obedience practice", category: "training" },
      { label: "Rehab", category: "training" },
      { label: "Show gaiting", category: "training" },
      { label: "Sports preparation", category: "training" },
      { label: "Stacking", category: "training" },
      { label: "Stamina / Endurance", category: "training" },
      { label: "Strength training", category: "training" },
      { label: "Treadmill", category: "training" },
      { label: "Trot work", category: "training" },
      { label: "Walk", category: "training" },
    ],
  },
  {
    group: "Grooming",
    items: [
      { label: "Clean clipper blades", category: "grooming" },
      { label: "Clipper work", category: "grooming" },
      { label: "Deep condition", category: "grooming" },
      { label: "De-matt problem areas", category: "grooming" },
      { label: "Ears", category: "grooming" },
      { label: "Full groom", category: "grooming" },
      { label: "Line brush", category: "grooming" },
      { label: "Line comb", category: "grooming" },
      { label: "Maintenance bath", category: "grooming" },
      { label: "Maintenance trim", category: "grooming" },
      { label: "Nails — grind", category: "grooming" },
      { label: "Nails — trim", category: "grooming" },
      { label: "Oil blades", category: "grooming" },
      { label: "Scissor work", category: "grooming" },
      { label: "Shave feet / Clean feet", category: "grooming" },
      { label: "Sharpen clipper blades", category: "grooming" },
      { label: "Sharpen scissors", category: "grooming" },
      { label: "Show groom", category: "grooming" },
    ],
  },
  {
    group: "Vet care",
    items: [
      { label: "Refill heartworm, flea, tick", category: "vet" },
      { label: "Refill meds", category: "vet" },
      { label: "Yearly vet visit / vacc", category: "vet" },
    ],
  },
  {
    group: "Other",
    items: [
      { label: "Other", category: "other" },
    ],
  },
];

export const ACTIVITIES = ACTIVITY_GROUPS.flatMap((g) => g.items);

/**
 * Merge server-side per-dog custom activities + pinned built-in favourites
 * into the built-in group list. Returns { groups, flat } — groups drives
 * <optgroup>, flat drives the "does the current selection still exist" checks
 * that the pages already do.
 *
 * Order (top → bottom): "★ Your favourites" (pinned built-ins), "Your custom
 * activities", then the built-in groups minus any pinned labels so items
 * never appear twice.
 *
 * customItems shape: [{ id, dog_id, label, category, position, ...}]
 * pinnedLabels shape: [string] — labels in pin order.
 */
export function mergeCustomActivities(customItems, pinnedLabels) {
  const customs = Array.isArray(customItems) ? customItems : [];
  const pinned = Array.isArray(pinnedLabels) ? pinnedLabels : [];

  if (customs.length === 0 && pinned.length === 0) {
    return { groups: ACTIVITY_GROUPS, flat: ACTIVITIES };
  }

  // Build a quick lookup so we know each pinned label's category and can
  // drop it from its original built-in group.
  const byLabel = new Map(ACTIVITIES.map((a) => [a.label, a]));
  const pinSet = new Set(pinned);

  const favGroup =
    pinned.length > 0
      ? {
          group: "★ Your favourites",
          // Only include labels we still know about (guards against a built-in
          // being removed later).
          items: pinned
            .filter((lbl) => byLabel.has(lbl))
            .map((lbl) => byLabel.get(lbl)),
        }
      : null;

  const customGroup =
    customs.length > 0
      ? {
          group: "Your custom activities",
          // Preserve server order (driven by `position` from the reorder endpoint).
          items: customs.map((c) => ({ label: c.label, category: c.category || "other" })),
        }
      : null;

  const builtinsPruned = ACTIVITY_GROUPS.map((g) => ({
    group: g.group,
    items: g.items.filter((a) => !pinSet.has(a.label)),
  })).filter((g) => g.items.length > 0);

  const groups = [
    ...(favGroup ? [favGroup] : []),
    ...(customGroup ? [customGroup] : []),
    ...builtinsPruned,
  ];
  return { groups, flat: groups.flatMap((g) => g.items) };
}
